import { useMapsLibrary, useApiLoadingStatus, APILoadingStatus } from "@vis.gl/react-google-maps";
import { createContext, FC, PropsWithChildren, useEffect, useState } from "react";
import { toast } from "react-toastify";

interface GoogleMapsLibraryContextProps {

    geocodingService: google.maps.Geocoder | undefined

    geocodeZipcode: (zipCode: string) => Promise<google.maps.LatLngLiteral | undefined>

    geocodeAddress: (street: string,
        city: string,
        state: string,
        zip: string,
        country: string,
        street2?: string
    ) => Promise<google.maps.GeocoderResult | undefined>

}

export const GoogleMapsLibraryContext = createContext<GoogleMapsLibraryContextProps | undefined>(undefined);

export const GoogleMapsLibraryProvider: FC<PropsWithChildren> = ({ children }) => {

    const loadingStatus = useApiLoadingStatus();

    const [apiUnableToLoad, setApiUnableToLoad] = useState<boolean>(false);

    const geocodingApiLoaded = useMapsLibrary('geocoding');

    const [geocodingService, setGeocodingService] = useState<google.maps.Geocoder>();

    const [userLocation, setUserLocation] = useState();

    const geocodeZipcode = (zipCode: string): Promise<google.maps.LatLngLiteral | undefined> => {

        return new Promise(async (resolve, reject) => {

            if (!geocodingService) {

                reject(undefined);

            } else {

                await geocodingService.geocode({ address: zipCode }, (results, status) => {

                    if (results) {

                        resolve({ lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() });

                    } else {

                        resolve(undefined);

                    }

                }).catch(error => resolve(undefined));

            }

        });

    }

    const geocodeAddress = (street: string, city: string, state: string, zip: string, country: string, street2: string | undefined = undefined): Promise<google.maps.GeocoderResult | undefined> => {

        return new Promise(async (resolve, reject) => {

            try {

                if (!geocodingService) {

                    reject(undefined);

                } else {

                    const address = `${street2 ? `${street} ${street2},` : `${street},`} ${city}, ${state} ${zip} ${country}`;

                    await geocodingService.geocode({ address: address }, (results, status) => {

                        if (results) {

                            resolve(results[0]);

                        } else {

                            resolve(undefined);

                        }

                    })

                }

            } catch (error) {

                reject(undefined)

            }

        });

    }

    /*
        Check api is loaded and populate the service into
        state
    */
    useEffect(() => {

        if (!geocodingApiLoaded) return;

        setGeocodingService(new window.google.maps.Geocoder());

    }, [geocodingApiLoaded]);

    /* 
        Check if api failed to load due to block or some other issue
    */
    useEffect(() => {

        // 1. Native library detection
        if (loadingStatus === APILoadingStatus.FAILED) {

            setApiUnableToLoad(true);

            return;

        }

        // 2. Global fallback catch if the adblocker strips out the script 
        // before the vis.gl engine can track the error natively
        const handleScriptError = (event: any) => {

            if (

                event.target &&

                event.target.tagName === 'SCRIPT' &&

                event.target.src.includes('://googleapis.com')

            ) {

                setApiUnableToLoad(true);

            }

        };

        window.addEventListener('error', handleScriptError, true);

        return () => window.removeEventListener('error', handleScriptError, true);

    }, [loadingStatus]);

    useEffect(() => {

        if (apiUnableToLoad)
            toast.error('Google API services blocked or failed to load.  Please check browser extensions or ad blockers.  Google API services are required for this APP to run properly')

    }, [apiUnableToLoad]);

    return (

        <GoogleMapsLibraryContext.Provider
            value={{ geocodingService, geocodeZipcode, geocodeAddress }}>

            {children}

        </GoogleMapsLibraryContext.Provider>

    )

}